import React, { useContext, useMemo } from "react";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import Pagetitle from "../Components/Pagetitle";
import { Link } from "react-router-dom";
import { CartContext } from "../CartContext";

function Cart() {
  const { cart, setCart } = useContext(CartContext);

  const totalQuantity = useMemo(
    () => cart.reduce((total, item) => total + item.quantity, 0),
    [cart]
  );

  const shipping = useMemo(
    () => (totalQuantity === 1 ? 100 : 0),
    [totalQuantity]
  );

  // Discounted subtotal
  const cartSubtotal = useMemo(
    () =>
      cart.reduce(
        (total, item) => total + item.discount_price * item.quantity,
        0
      ),
    [cart]
  );

  // Original MRP total
  const originalTotal = useMemo(
    () =>
      cart.reduce(
        (total, item) =>
          total + (item.price || item.discount_price) * item.quantity,
        0
      ),
    [cart]
  );

  // Discount amount
  const discountAmount = useMemo(
    () => originalTotal - cartSubtotal,
    [originalTotal, cartSubtotal]
  );

  // Discount percentage
  const discountPercent = useMemo(() => {
    if (originalTotal === 0) return 0;
    return Math.round((discountAmount / originalTotal) * 100);
  }, [discountAmount, originalTotal]);

  const incrementQuantity = (id) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === id ? { ...item, quantity: item.quantity + 1 } : item
      )
    );
  };

  const decrementQuantity = (id) => {
    setCart((prevCart) =>
      prevCart
        .map((item) => {
          if (item.id === id) {
            if (item.quantity === 1) return null;
            return { ...item, quantity: item.quantity - 1 };
          }
          return item;
        })
        .filter(Boolean)
    );
  };

  return (
    <>
      <main className="container-fluid">
        <Header />
        <Pagetitle />

        <section className="row px-sm-5 my-5">
          <div className="cart-container p-4 shadow rounded">
            {/* Header */}
            <div className="d-flex">
              <h4 className="w-100">My Shopping List:</h4>
              <h5 className="text-muted w-100 text-end">
                {cart.length} Item(s)
              </h5>
            </div>
            <hr />

            {/* Cart Items */}
            {cart.length > 0 ? (
              cart.map((item) => (
                <div
                  key={item.id}
                  className="cart-item d-flex align-items-center mb-3 p-3 border-bottom"
                >
                  <Link to={`/product/${item.slug}`} className="me-3">
                    <img
                      src={item.image}
                      className="product-img me-3"
                      alt={item.title}
                    />
                  </Link>
                  <div className="flex-grow-1">
                    <h6 className="fw-bold">{item.title}</h6>

                    {/* Price */}
                    <div className="price-box">
                      <span className="fw-bold fs-6">
                        ₹{item.discount_price}
                      </span>

                      {item.price && item.price > item.discount_price && (
                        <>
                          <span className="text-muted text-decoration-line-through ms-2">
                            ₹{item.price}
                          </span>
                          <span className="text-success ms-2 small fw-semibold">
                            {Math.round(
                              ((item.price - item.discount_price) /
                                item.price) *
                                100
                            )}
                            % OFF
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Quantity Controls */}
                  <div className="d-flex align-items-center">
                    <button
                      className="btn-qty me-2"
                      onClick={() => decrementQuantity(item.id)}
                    >
                      <i
                        className={`fa-solid ${
                          item.quantity === 1 ? "fa-trash" : "fa-minus"
                        }`}
                      ></i>
                    </button>

                    <span className="mx-2 fw-bold">{item.quantity}</span>

                    <button
                      className="btn-qty ms-2"
                      onClick={() => incrementQuantity(item.id)}
                    >
                      <i className="fa-solid fa-plus"></i>
                    </button>
                  </div>

                  {/* Line Total */}
                  <h5 className="ms-4 fw-bold">
                    ₹{item.discount_price * item.quantity}
                  </h5>
                </div>
              ))
            ) : (
              <p className="text-center text-muted h2">🛒 Your cart is empty</p>
            )}

            {/* CART TOTALS */}
            {cart.length > 0 && (
              <>
                <h5 className="fw-bold mt-3">Cart Totals</h5>

                <div className="d-flex justify-content-between">
                  <span>MRP Total</span>
                  <span className="fw-semibold text-secondary">
                    ₹{originalTotal}
                  </span>
                </div>

                <div className="d-flex justify-content-between">
                  <span>Discount</span>
                  <span className="text-success fw-semibold">
                    - ₹{discountAmount} ({discountPercent}% OFF)
                  </span>
                </div>

                <div className="d-flex justify-content-between">
                  <span>Subtotal</span>
                  <span className="fw-bold">₹{cartSubtotal} (incl. GST)</span>
                </div>

                <div className="d-flex justify-content-between">
                  <span>Shipping</span>
                  <span className="fw-bold">₹{shipping}</span>
                </div>

                <div className="d-flex justify-content-between total-container">
                  <span className="fw-bold fs-5">Total Payable</span>
                  <span className="fw-bold fs-5 text-success">
                    ₹{cartSubtotal + shipping}
                  </span>
                </div>

                <p className="text-success small mt-2">
                  <i className="fa-solid fa-tag"></i>
                  You saved ₹{discountAmount} on this order
                </p>

                <div className="row py-3 text-center">
                  <Link to="/checkout">
                    <button className="btn-style1">Checkout Now</button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}

export default Cart;
